import React, { useEffect, useState } from 'react';
import { RedocStandalone } from 'redoc';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { getOpenApiSpec } from '../../services/apiPlaygroundService';

interface ApiDocumentationProps {
  apiKey: string;
}

const ApiDocumentation: React.FC<ApiDocumentationProps> = ({ apiKey }) => {
  const [spec, setSpec] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSpec = async () => {
      try {
        setLoading(true);
        setError(null);
        const openApiSpec = await getOpenApiSpec();
        
        if (openApiSpec) {
          setSpec(openApiSpec);
        } else {
          // Fallback to a basic spec if we can't fetch the real one
          setSpec(createFallbackSpec());
        }
      } catch (err) {
        console.error('Error loading OpenAPI spec:', err);
        setError('Failed to load API documentation');
        setSpec(createFallbackSpec());
      } finally {
        setLoading(false);
      }
    };

    loadSpec();
  }, []);

  const createFallbackSpec = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.crawlycarl.com/api/v1';
    
    return {
      openapi: '3.0.0',
      info: {
        title: 'CrawlyCarl API',
        description: 'AI-powered web scraping API using Gemini 2.0 Flash',
        version: '1.0.0',
      },
      servers: [
        {
          url: baseUrl,
          description: 'Production API',
        },
      ],
      security: [
        {
          ApiKeyAuth: [],
        },
      ],
      components: {
        securitySchemes: {
          ApiKeyAuth: {
            type: 'apiKey',
            in: 'header',
            name: 'X-API-Key',
          },
        },
        schemas: {
          SyncCrawlRequest: {
            type: 'object',
            required: ['url', 'target_data'],
            properties: {
              url: {
                type: 'string',
                format: 'uri',
                description: 'The URL to crawl',
                example: 'https://example.com',
              },
              target_data: {
                oneOf: [
                  { type: 'string' },
                  { type: 'object' },
                ],
                description: 'Data to extract (string or JSON object)',
                example: 'Extract the main content and key information',
              },
              deepsearch: {
                type: 'boolean',
                default: false,
                description: 'Enable deep search navigation',
              },
              intelligent_search: {
                type: 'boolean',
                default: true,
                description: 'Analyze hyperlinks and navigate based on relevance',
              },
              proxy_country: {
                type: 'string',
                default: 'US',
                enum: ['US', 'UK', 'CA', 'HK', 'SG', 'ES', 'FR', 'IT', 'DE', 'AT', 'RO', 'BR', 'IN'],
                description: 'Country code for proxy location',
              },
              debugmode: {
                type: 'boolean',
                default: false,
                description: 'Include debug information in response',
              },
            },
          },
          StandardResponse: {
            type: 'object',
            properties: {
              status: {
                type: 'string',
                enum: ['success', 'error'],
                description: 'Status of the operation',
              },
              data: {
                type: 'object',
                description: 'The actual result data',
              },
              meta: {
                type: 'object',
                properties: {
                  total_cost: {
                    type: 'integer',
                    description: 'Total cost in credits',
                  },
                  processing_time_ms: {
                    type: 'integer',
                    description: 'Processing time in milliseconds',
                  },
                },
              },
            },
          },
        },
      },
      paths: {
        '/crawl-sync': {
          post: {
            summary: 'Synchronous Web Crawling',
            description: 'Crawl a website and extract data synchronously',
            security: [{ ApiKeyAuth: [] }],
            requestBody: {
              required: true,
              content: {
                'application/json': {
                  schema: {
                    $ref: '#/components/schemas/SyncCrawlRequest',
                  },
                },
              },
            },
            responses: {
              200: {
                description: 'Successful response',
                content: {
                  'application/json': {
                    schema: {
                      $ref: '#/components/schemas/StandardResponse',
                    },
                  },
                },
              },
              400: {
                description: 'Bad request',
              },
              401: {
                description: 'Unauthorized - Invalid API key',
              },
              402: {
                description: 'Payment required - Insufficient credits',
              },
              500: {
                description: 'Internal server error',
              },
            },
          },
        },
      },
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading API documentation...</span>
      </div>
    );
  }

  if (error && !spec) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!spec) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600 mb-4">API documentation not available</div>
        <p className="text-sm text-gray-500">
          You can access the interactive API documentation directly at:
        </p>
        <a
          href={`${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'https://api.crawlycarl.com'}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Open API Documentation
        </a>
      </div>
    );
  }

  return (
    <div className="api-documentation-container">
      <RedocStandalone
        spec={spec}
        options={{
          theme: {
            colors: {
              primary: {
                main: '#2563eb'
              }
            },
            typography: {
              fontSize: '14px',
              fontFamily: 'inherit'
            }
          },
          scrollYOffset: 60,
          hideDownloadButton: true,
          disableSearch: false,
          expandResponses: '200,201',
          jsonSampleExpandLevel: 2,
          hideSingleRequestSampleTab: true,
          showExtensions: true,
          sortPropsAlphabetically: true,
          payloadSampleIdx: 0
        }}
      />

      <div className="mt-6 space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">API Key Authentication</h4>
          <p className="text-sm text-blue-700">
            Your API key (<code className="bg-blue-100 px-1 rounded">{apiKey.slice(0, 8)}...</code>)
            will be automatically included in requests when using the playground.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            For manual testing, include your API key in the <code>X-API-Key</code> header.
          </p>
        </div>

        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Need More Details?</h4>
          <p className="text-sm text-gray-600 mb-3">
            For comprehensive guides, examples, and detailed documentation, visit our main documentation site.
          </p>
          <a
            href="https://crawlycarl.com/api-docs/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentTextIcon className="h-4 w-4 mr-1" />
            View Full Documentation
          </a>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
